import styled, { css, keyframes } from 'styled-components';

type Phase = 'listening' | 'thinking' | 'speaking';

const NAV_HEIGHT = 72;
const MOBILE_ORBIT_RADIUS = 88;
const MOBILE_ORBIT_CENTER_X = 182;
const MOBILE_ORBIT_CENTER_Y = 138;

const navButtonActive = css`
  color: #ffffff;
  background: rgba(0, 255, 200, 0.08);
`;

/* ── Keyframes ─────────────────────────────────────────────────────────── */

const listeningOrbit = keyframes`
  0%   { transform: rotate(0deg) scale(0.98); opacity: 0.4; }
  50%  { transform: rotate(180deg) scale(1.02); opacity: 0.95; }
  100% { transform: rotate(360deg) scale(0.98); opacity: 0.4; }
`;

const listeningPulse = keyframes`
  0%, 100% { opacity: 0.2; transform: scale(0.92); }
  50%      { opacity: 0.68; transform: scale(1.02); }
`;

const thinkingSweep = keyframes`
  0%   { transform: translateX(-32%); opacity: 0; }
  22%  { opacity: 0.85; }
  100% { transform: translateX(32%); opacity: 0; }
`;

const thinkingDot = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
  40%           { transform: translateY(-3px); opacity: 1; }
`;

const speakingGlow = keyframes`
  0%   { box-shadow: 0 0 12px rgba(200, 100, 255, 0.3), 0 0 24px rgba(100, 180, 255, 0.15); }
  50%  { box-shadow: 0 0 20px rgba(200, 100, 255, 0.5), 0 0 40px rgba(100, 180, 255, 0.25); }
  100% { box-shadow: 0 0 12px rgba(200, 100, 255, 0.3), 0 0 24px rgba(100, 180, 255, 0.15); }
`;

const speakingOrbit = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const speakingPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.95); }
  50%      { opacity: 1; transform: scale(1.05); }
`;

const thinkingBlink = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(0.96); }
  50%      { opacity: 1; transform: scale(1.04); }
`;

/* ── Phase color helpers ──────────────────────────────────────────────── */

const PHASE_COLORS = {
  listening: { primary: '#00ffc8', glow: 'rgba(0, 255, 200,', border: 'rgba(0, 255, 200,' },
  thinking: { primary: '#c4fff4', glow: 'rgba(138, 255, 229,', border: 'rgba(138, 255, 229,' },
  speaking: { primary: '#c878ff', glow: 'rgba(200, 120, 255,', border: 'rgba(200, 120, 255,' },
} as const;

/* ── Layout ────────────────────────────────────────────────────────────── */

export const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 600;
  pointer-events: none;
`;

export const BottomNav = styled.nav`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: stretch;
  padding: 0;
  pointer-events: none;
`;

export const MobileDock = styled.div`
  position: relative;
  width: 100%;
  pointer-events: auto;
`;

export const NavItems = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto minmax(220px, 1fr);
  align-items: center;
  width: 100%;
  min-height: ${NAV_HEIGHT}px;
  padding: 6px 14px;
  border: 1px solid rgba(0, 255, 200, 0.1);
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  border-radius: 0;
  background:
    linear-gradient(180deg, rgba(10, 18, 34, 0.36), rgba(5, 10, 20, 0.3)), rgba(6, 10, 20, 0.22);
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(20px);
  pointer-events: auto;

  @media (max-width: 900px) {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 4px;
    min-height: 64px;
    padding: 6px 4px calc(env(safe-area-inset-bottom, 0px) + 6px);
  }
`;

export const AssistantSlot = styled.div`
  display: flex;
  justify-content: flex-start;
  min-width: 0;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

export const CenterNavWrap = styled.div`
  position: relative;
  min-width: 0;
`;

/* ── Mobile menu ──────────────────────────────────────────────────────── */

export const MobileMenuPanel = styled.div<{ $open: boolean }>`
  position: absolute;
  right: 10px;
  bottom: calc(100% + 8px);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 158px;
  max-width: calc(100vw - 20px);
  padding: 10px 3px;
  border: 1px solid rgba(0, 255, 200, 0.16);
  border-radius: 16px;
  background: rgba(8, 14, 28, 0.96);
  backdrop-filter: blur(20px);
  box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.45);
  z-index: 24;
  pointer-events: auto;

  @media (min-width: 901px) {
    display: none;
  }
`;

export const MobileMenuItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 10px 5px;
  border: 0;
  border-radius: 12px;
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 200, 0.08)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#00ffc8' : 'rgba(255, 255, 255, 0.66)')};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:active {
    background: rgba(0, 255, 200, 0.12);
  }
`;

export const MobileMenuItemLabel = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? '#00ffc8' : 'rgba(255, 255, 255, 0.82)')};

  @media (max-width: 900px) {
    font-size: 9px;
    letter-spacing: 0.1em;
  }
`;

export const MobileCommunityTray = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 0 6px;
  width: 100%;
`;

export const MobileCommunityIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.58);
  text-decoration: none;
  background: rgba(255, 255, 255, 0.02);
  box-shadow: inset 0 0 0 1px rgba(0, 255, 200, 0.12);
  transition:
    color 0.15s ease,
    background 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover,
  &:active {
    color: #00ffc8;
    background: rgba(0, 255, 200, 0.08);
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 1px rgba(0, 255, 200, 0.24);
  }
`;

export const CenterNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;

  @media (min-width: 390px) and (max-width: 900px) {
    justify-content: space-between;
    width: 100%;
    overflow: hidden;
    gap: 1px;
    padding-bottom: 0;
  }

  @media (max-width: 389px) {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: visible;
    gap: 0;
    padding-bottom: 0;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const ScrollHint = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: 389px) {
    display: flex;
    position: absolute;
    top: 50%;
    right: 0;
    align-items: center;
    justify-content: flex-end;
    width: 40px;
    height: 100%;
    padding-right: 2px;
    pointer-events: none;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transform: translateY(-50%);
    transition: opacity 0.25s ease;
    background: linear-gradient(90deg, rgba(6, 10, 20, 0), rgba(6, 10, 20, 0.88) 55%);

    span {
      display: block;
      width: 10px;
      height: 10px;
      border-top: 2px solid rgba(0, 255, 200, 0.92);
      border-right: 2px solid rgba(0, 255, 200, 0.92);
      transform: rotate(45deg);
      filter: drop-shadow(0 0 8px rgba(0, 255, 200, 0.35));
      animation: scrollHintFloat 1.2s ease-in-out infinite;
    }

    @keyframes scrollHintFloat {
      0%,
      100% {
        transform: translateX(0) rotate(45deg);
      }

      50% {
        transform: translateX(4px) rotate(45deg);
      }
    }
  }
`;

export const RightSpacer = styled.div`
  min-width: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

/* ── Nav buttons ──────────────────────────────────────────────────────── */

export const NavButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 70px;
  padding: 7px 14px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.34);
  cursor: pointer;
  pointer-events: auto;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
  position: relative;

  ${(props) => props.$active && navButtonActive}

  &::after {
    content: '';
    position: absolute;
    right: 18px;
    bottom: 0;
    left: 18px;
    height: 0;
    border-radius: 999px 999px 0 0;
    background: #00ffc8;
    box-shadow: 0 0 12px rgba(0, 255, 200, 0.35);
    opacity: 0;
    transition:
      opacity 0.2s ease,
      height 0.2s ease;
  }

  ${(props) =>
    props.$active &&
    css`
      &::after {
        height: 3px;
        opacity: 1;
      }
    `}

  &:hover {
    color: rgba(255, 255, 255, 0.72);
    background: rgba(0, 255, 200, 0.05);
  }

  @media (max-width: 900px) {
    min-width: clamp(52px, 12vw, 62px);
    gap: 2px;
    min-height: 52px;
    padding: 6px 6px 5px;
    border-radius: 12px;

    &:last-child {
      min-width: clamp(58px, 14vw, 68px);
    }
  }
`;

/* ── EQ bars canvas (above button) ────────────────────────────────────── */

export const EqCanvas = styled.canvas`
  position: absolute;
  pointer-events: none;
  z-index: 2;
`;

/* ── Sophie assistant button ──────────────────────────────────────────── */

export const AssistantButton = styled.button<{ $active: boolean; $phase?: Phase | null }>`
  display: flex;
  position: relative;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 5px 6px;
  border: 1px solid rgba(0, 255, 200, 0.16);
  border-radius: 16px;
  background: rgba(7, 14, 28, 0.92);
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  overflow: visible;
  pointer-events: auto;
  outline: 1px solid transparent;
  outline-offset: 2px;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease;

  ${({ $active, $phase }) =>
    $active &&
    !$phase &&
    css`
      border-color: rgba(0, 255, 200, 0.5);
      background: rgba(0, 35, 28, 0.92);
      box-shadow: 0 0 22px rgba(0, 255, 200, 0.14);
    `}

  ${({ $phase }) =>
    $phase === 'listening' &&
    css`
      border-color: rgba(0, 255, 200, 0.5);
      background: rgba(0, 35, 28, 0.92);
      box-shadow: 0 0 22px rgba(0, 255, 200, 0.2);
    `}

  ${({ $phase }) =>
    $phase === 'thinking' &&
    css`
      border-color: rgba(138, 255, 229, 0.45);
      background:
        linear-gradient(110deg, rgba(0, 255, 200, 0.02), rgba(140, 255, 235, 0.08), rgba(0, 255, 200, 0.02)),
        rgba(7, 14, 28, 0.92);
      box-shadow: 0 0 18px rgba(138, 255, 229, 0.12);
    `}

  ${({ $phase }) =>
    $phase === 'speaking' &&
    css`
      border-color: rgba(200, 120, 255, 0.55);
      background: rgba(28, 7, 34, 0.92);
      animation: ${speakingGlow} 2s ease-in-out infinite;
    `}

  &:hover {
    border-color: rgba(0, 255, 200, 0.34);
  }

  @media (max-width: 900px) {
    gap: 5px;
    min-height: 52px;
    padding: 6px 7px;
    border-radius: 14px;
    width: auto;
    max-width: 118px;
  }
`;

export const AssistantMain = styled.div`
  display: flex;
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 5px;

  @media (max-width: 900px) {
    gap: 5px;
  }
`;

export const AssistantIconWrap = styled.span<{ $active: boolean; $phase?: Phase | null }>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease;

  /* Default / listening / idle */
  border: 1px solid ${(props) => (props.$active ? 'rgba(0, 255, 200, 0.55)' : 'rgba(0, 255, 200, 0.18)')};
  background: ${(props) => (props.$active ? 'rgba(0, 255, 200, 0.14)' : 'rgba(0, 255, 200, 0.06)')};
  box-shadow: ${(props) => (props.$active ? '0 0 16px rgba(0, 255, 200, 0.2)' : 'none')};

  /* Thinking — pulsing blink */
  ${({ $phase }) =>
    $phase === 'thinking' &&
    css`
      border-color: rgba(138, 255, 229, 0.55);
      background: rgba(0, 255, 200, 0.12);
      animation: ${thinkingBlink} 1.6s ease-in-out infinite;
    `}

  /* Speaking — pink/magenta glow */
  ${({ $phase }) =>
    $phase === 'speaking' &&
    css`
      border-color: rgba(200, 120, 255, 0.7);
      background: rgba(200, 120, 255, 0.18);
      box-shadow:
        0 0 18px rgba(200, 120, 255, 0.35),
        0 0 36px rgba(100, 180, 255, 0.15);
    `}

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    transition:
      stroke 0.25s ease,
      color 0.25s ease;
    stroke: ${({ $phase, $active }) =>
      $phase === 'speaking'
        ? '#c878ff'
        : $active
          ? '#00ffc8'
          : 'rgba(255, 255, 255, 0.72)'};
    color: ${({ $phase, $active }) =>
      $phase === 'speaking'
        ? '#c878ff'
        : $active
          ? '#00ffc8'
          : 'rgba(255, 255, 255, 0.72)'};
  }

  svg rect {
    stroke: none;
  }

  @media (max-width: 900px) {
    width: 30px;
    height: 30px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const AssistantIconOrbit = styled.span<{
  $visible: boolean;
  $phase: Phase;
}>`
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
  }

  /* Rotating half-border */
  &::before {
    border: 1px solid
      ${({ $phase }) =>
        $phase === 'speaking'
          ? 'rgba(200, 120, 255, 0.5)'
          : $phase === 'thinking'
            ? 'rgba(138, 255, 229, 0.34)'
            : 'rgba(0, 255, 200, 0.34)'};
    border-right-color: transparent;
    border-bottom-color: transparent;
    animation: ${({ $phase }) => ($phase === 'speaking' ? speakingOrbit : listeningOrbit)}
      ${({ $phase }) => ($phase === 'speaking' ? '1.4s' : $phase === 'thinking' ? '2s' : '1.2s')}
      linear infinite;
  }

  /* Inner dashed ring */
  &::after {
    inset: 4px;
    border: 1px dashed
      ${({ $phase }) =>
        $phase === 'speaking'
          ? 'rgba(200, 160, 255, 0.35)'
          : $phase === 'thinking'
            ? 'rgba(200, 255, 247, 0.28)'
            : 'rgba(0, 255, 200, 0.2)'};
    animation: ${({ $phase }) => ($phase === 'speaking' ? speakingPulse : listeningPulse)}
      ${({ $phase }) => ($phase === 'speaking' ? '1.2s' : $phase === 'thinking' ? '1.6s' : '1.1s')}
      ease-in-out infinite;
  }

  @media (max-width: 900px) {
    inset: -4px;
  }
`;

export const AssistantText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
`;

export const AssistantTitle = styled.span<{ $active: boolean }>`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);

  @media (max-width: 900px) {
    font-size: 9px;
    letter-spacing: 0.1em;
  }
`;

export const AssistantStatus = styled.span<{ $phase?: Phase | null }>`
  font-size: 10px;
  letter-spacing: 0.04em;
  min-width: 84px;
  text-align: left;
  color: ${({ $phase }) =>
    $phase === 'listening'
      ? 'rgba(130, 255, 226, 0.9)'
      : $phase === 'thinking'
        ? 'rgba(196, 255, 244, 0.86)'
        : $phase === 'speaking'
          ? 'rgba(220, 170, 255, 0.92)'
          : 'rgba(255, 255, 255, 0.38)'};
  white-space: nowrap;
  transition: color 0.2s ease;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const AssistantPhaseGlow = styled.span<{
  $visible: boolean;
  $phase: Phase;
}>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $phase }) =>
      $phase === 'speaking'
        ? 'radial-gradient(circle at 22% 50%, rgba(200, 120, 255, 0.14), rgba(100, 180, 255, 0.06) 62%)'
        : $phase === 'thinking'
          ? 'linear-gradient(90deg, rgba(0, 255, 200, 0), rgba(168, 255, 236, 0.14), rgba(0, 255, 200, 0))'
          : 'radial-gradient(circle at 22% 50%, rgba(0, 255, 200, 0.14), rgba(0, 255, 200, 0) 62%)'};
    animation: ${({ $phase }) =>
        $phase === 'thinking' ? thinkingSweep : $phase === 'speaking' ? speakingPulse : listeningPulse}
      ${({ $phase }) =>
        $phase === 'thinking'
          ? '1.9s linear infinite'
          : $phase === 'speaking'
            ? '2s ease-in-out infinite'
            : '1.15s ease-in-out infinite'};
  }
`;

/* ── Desktop right slot (fixed size to prevent button resize) ─────────── */

export const RightSlot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

/* ── Thinking dots ────────────────────────────────────────────────────── */

export const ThinkingDots = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;

  /* Inside RightSlot on desktop: overlay in place, no width change */
  ${RightSlot} & {
    position: absolute;
    width: 28px;
    margin-left: 0;
    justify-content: center;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  /* Mobile: fixed size, opacity-only to prevent jitter */
  width: 18px;
  margin-left: 4px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  transition: opacity 0.2s ease;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(196, 255, 244, 0.92);
    box-shadow: 0 0 10px rgba(168, 255, 236, 0.35);
  }

  ${({ $active }) =>
    $active &&
    css`
      span {
        animation: ${thinkingDot} 1s ease-in-out infinite;
      }

      span:nth-child(2) {
        animation-delay: 0.16s;
      }

      span:nth-child(3) {
        animation-delay: 0.32s;
      }
    `}

  @media (max-width: 900px) {
    width: 18px;
    gap: 3px;
    margin-left: 0;

    span {
      width: 3px;
      height: 3px;
    }
  }
`;

/* ── Mic mute button ─────────────────────────────────────────────────── */

const micPulse = keyframes`
  0%, 100% {
    border-color: rgba(255, 80, 80, 0.3);
    background: rgba(255, 80, 80, 0.08);
    color: rgba(255, 80, 80, 0.6);
    transform: scale(1);
  }
  50% {
    border-color: rgba(255, 80, 80, 0.7);
    background: rgba(255, 80, 80, 0.2);
    color: #ff5050;
    transform: scale(1.12);
  }
`;

export const MicButton = styled.button<{ $pulse?: boolean; $hidden?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  cursor: pointer;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.82);

  ${({ $hidden }) =>
    $hidden &&
    css`
      opacity: 0;
      pointer-events: none;
    `}

  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${micPulse} 1.4s ease-in-out infinite;
    `}

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }

  svg {
    width: 13px;
    height: 13px;
  }

  @media (max-width: 900px) {
    width: 24px;
    height: 24px;
    min-width: 24px;

    svg {
      width: 11px;
      height: 11px;
    }
  }
`;

/* ── Nav icons & labels ──────────────────────────────────────────────── */

export const NavIcon = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;

  @media (max-width: 900px) {
    width: 18px;
    height: 18px;
  }

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: ${(props) => (props.$active ? '#00ffc8' : 'rgba(255, 255, 255, 0.72)')};
    color: ${(props) => (props.$active ? '#00ffc8' : 'rgba(255, 255, 255, 0.72)')};
    filter: ${(props) =>
      props.$active ? 'drop-shadow(0 0 6px rgba(0, 255, 200, 0.38))' : 'none'};
    transition:
      stroke 0.2s ease,
      color 0.2s ease,
      filter 0.2s ease;

    @media (max-width: 900px) {
      width: 15px;
      height: 15px;
    }
  }

  svg rect {
    stroke: none;
  }

  svg path {
    stroke: currentColor;
  }

  ${NavButton}:hover & svg {
    stroke: rgba(0, 255, 200, 0.64);
    color: rgba(0, 255, 200, 0.64);
  }
`;

export const NavLabel = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(props) => (props.$active ? '#00ffc8' : 'rgba(255, 255, 255, 0.82)')};
  transition: color 0.2s ease;

  @media (max-width: 900px) {
    font-size: 9px;
    letter-spacing: 0.1em;
  }
`;

/* ── Community dropdown ──────────────────────────────────────────────── */

export const CommunityWrap = styled.div`
  position: relative;
  pointer-events: auto;
`;

export const CommunityDropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: calc(100% - 2px);
  left: 50%;
  display: ${(props) => (props.$open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 2px;
  min-width: 156px;
  padding: 8px;
  border: 1px solid rgba(0, 255, 200, 0.16);
  border-radius: 14px;
  background: rgba(8, 14, 28, 0.96);
  backdrop-filter: blur(20px);
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.45);
  transform: translateX(-50%);

  @media (max-width: 900px) {
    position: fixed;
    right: 12px;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 74px);
    left: auto;
    min-width: 170px;
    transform: none;
    z-index: 20;
  }
`;

export const CommunityLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.52);
  font-size: 13px;
  letter-spacing: 0.04em;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: rgba(0, 255, 200, 0.08);
    color: #00ffc8;
    box-shadow: inset 0 0 0 1px rgba(0, 255, 200, 0.12);
  }

  &:hover span:first-child {
    color: #00ffc8;
    transform: translateY(-1px);
  }
`;

export const CommunityIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.52);
  transition:
    color 0.15s ease,
    transform 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
  }
`;

export const CommunityTip = styled.div`
  position: absolute;
  bottom: -5px;
  left: 50%;
  width: 12px;
  height: 12px;
  border-right: 1px solid rgba(0, 255, 200, 0.16);
  border-bottom: 1px solid rgba(0, 255, 200, 0.16);
  background: rgba(8, 14, 28, 0.96);
  transform: translateX(-50%) rotate(45deg);

  @media (max-width: 900px) {
    display: none;
  }
`;

export const Caret = styled.span<{ $open: boolean; $direction?: 'up' | 'down' }>`
  width: 7px;
  height: 7px;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
  transform: ${({ $open, $direction }) => {
    if ($direction === 'down') {
      return $open ? 'rotate(-45deg)' : 'rotate(135deg)';
    }

    return 'rotate(-45deg)';
  }};
  transition: transform 0.18s ease;
`;
